import {
  addMinutes,
  differenceInMilliseconds,
  format,
  startOfDay,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type * as DocumentPicker from 'expo-document-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import type * as ImagePicker from 'expo-image-picker';
import * as WebBrowser from 'expo-web-browser';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { Image, Linking, Platform } from 'react-native';
import { UnistylesRuntime } from 'react-native-unistyles';
import type { StoreApi, UseBoundStore } from 'zustand';

import type { CarImage, CarImages } from '@/api/cars/types';
import type { PhoneCountry } from '@/components/input-phone/types';

export function openLinkInBrowser(url: string) {
  Linking.canOpenURL(url).then((canOpen) => canOpen && Linking.openURL(url));
}

export const openUrl = async (url: string) => {
  if (Platform.OS === 'web') {
    window.open(url, '_blank');
  } else {
    await WebBrowser.openBrowserAsync(url);
  }
};

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

export const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(
  _store: S
) => {
  let store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (let k of Object.keys(store.getState())) {
    (store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
  }

  return store;
};

export const snakeToTitleCase = (str: string): string => {
  return str
    ?.split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const titleCaseToSnake = (str: string): string => {
  if (!str) return '';
  return str.replace(/\s+/g, '_').toLowerCase();
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return '';

  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const snakeToCamelCase = (str: string): string => {
  if (!str) return '';

  return str
    .toLowerCase()
    .split('_')
    .map((word, index) => {
      if (index === 0) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');
};

export const camelCaseToTitleCase = (str: string): string => {
  if (!str) return '';

  return str
    .replace(/([a-z])([A-Z 0-9])/g, '$1 $2') // Insert space before uppercase letters
    .replace(/^./, (match) => match.toUpperCase());
};

export const camelCaseToSnakeCase = (str: string): string => {
  if (!str) return '';

  return str
    .replace(/([a-z])([A-Z0-9])/g, '$1_$2') // Insert underscore before uppercase letters and numbers
    .toLowerCase(); // Convert the entire string to lowercase
};

export const titleize = (str: string): string => {
  if (!str) return '';

  return str.slice(0, 1).toUpperCase() + str.slice(1).toLowerCase();
};

export const getBackgroundImageUri = (brand: string, model: string): string => {
  if (brand === 'tesla' && model === 'cybertruck') {
    return require('../../public/images/cars/tesla_cybertruck_grey.png');
  }

  if (brand === 'rivian' && model === 'r1t') {
    return require('../../public/images/cars/rivian_r1t.png');
  } else if (brand === 'rivian' && model === 'r1s') {
    return require('../../public/images/cars/rivian_r1s.png');
  } else if (brand === 'tesla' && model === 'model_s') {
    return require('../../public/images/cars/tesla_model_s_white.png');
  } else if (brand === 'tesla' && model === 'model_x') {
    return require('../../public/images/cars/tesla_model_x_white.png');
  } else if (brand === 'tesla' && model === 'model_y') {
    return require('../../public/images/cars/tesla_model_y_white.png');
  } else if (brand === 'tesla' && model === 'model_3') {
    return require('../../public/images/cars/tesla_model_3_white.png');
  }

  return require('../../public/images/cars/model_unknown.png');
};

export const getSubscriptionBackgroundImageUri = (
  brand: string,
  model: string
): string => {
  const themeName = UnistylesRuntime.themeName;
  if (brand === 'tesla' && model === 'cybertruck') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/tesla-cybertruck-grey-dark.webp`);
    } else {
      return require(`../../public/images/subscriptions/cars/tesla-cybertruck-grey-light.webp`);
    }
  }

  if (brand === 'lucid' && model === 'air') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/lucid-air-grey-dark.webp`);
    } else {
      return require(`../../public/images/subscriptions/cars/lucid-air-grey-light.webp`);
    }
  }

  if (brand === 'rivian' && model === 'r1t') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/rivian-r1t-black-dark.png`);
    } else {
      return require(`../../public/images/subscriptions/cars/rivian-r1t-black-light.png`);
    }
  } else if (brand === 'rivian' && model === 'r1s') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/rivian-r1s-black-dark.png`);
    } else {
      return require(`../../public/images/subscriptions/cars/rivian-r1s-black-light.png`);
    }
  } else if (brand === 'tesla' && model === 'model_s') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/tesla-modelS-grey-dark.webp`);
    } else {
      return require(`../../public/images/subscriptions/cars/tesla-modelS-grey-light.webp`);
    }
  } else if (brand === 'tesla' && model === 'model_x') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/tesla-modelX-grey-dark.webp`);
    } else {
      return require(`../../public/images/subscriptions/cars/tesla-modelX-grey-light.webp`);
    }
  } else if (brand === 'tesla' && model === 'model_y') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/tesla-modelY-grey-dark.webp`);
    } else {
      return require(`../../public/images/subscriptions/cars/tesla-modelY-grey-light.webp`);
    }
  } else if (brand === 'tesla' && model === 'model_3') {
    if (themeName === 'dark') {
      return require(`../../public/images/subscriptions/cars/tesla-model3-grey-dark.webp`);
    } else {
      return require(`../../public/images/subscriptions/cars/tesla-model3-grey-light.webp`);
    }
  }

  return require('../../public/images/cars/model_unknown.png');
};

export const getDailyInsuranceCost = (
  insurance: { percentage: number; fixedAmount?: number | null },
  rate: number
): number =>
  insurance.fixedAmount != null
    ? insurance.fixedAmount
    : insurance.percentage * rate;

export const isInsuranceFree = (insurance: {
  percentage: number;
  fixedAmount?: number | null;
}): boolean => insurance.percentage === 0 && !insurance.fixedAmount;

// Subscriptions price insurance against the period total (carCost), not a
// daily rate. fixedAmount only exists on the weekly EVFMC unlimited-mileage
// option today — it's a per-day dollar amount, so it needs scaling by the 7
// days in that period to get the period total. Every other period only ever
// sees percentage-based options, so it keeps the original unscaled formula.
export const getSubscriptionInsuranceCost = (
  insurance: { percentage: number; fixedAmount?: number | null },
  carCost: number,
  period: 'weekly' | 'monthly' | '6Months'
): number => {
  if (period !== 'weekly') {
    return insurance.percentage * carCost;
  }

  return getDailyInsuranceCost(insurance, carCost / 7) * 7;
};

export const getCarContentId = (brandName: string, modelName: string): string =>
  `${brandName}-${modelName}`.toLowerCase().replace(/\s+/g, '-');

export const getCarContentData = (
  car: { brandName: string; modelName: string; modelType?: string },
  {
    category,
    quantity = 1,
    itemPrice,
  }: { category: string; quantity?: number; itemPrice?: number }
): Record<string, unknown> => {
  const contentId = getCarContentId(car.brandName, car.modelName);
  const contentName = car.modelType
    ? `${car.brandName} ${car.modelName} - ${car.modelType}`
    : `${car.brandName} ${car.modelName}`;

  return {
    content_type: 'product',
    content_category: category,
    content_ids: [contentId],
    content_name: contentName,
    contents: [
      {
        id: contentId,
        quantity,
        ...(itemPrice != null ? { item_price: itemPrice } : {}),
      },
    ],
  };
};

export const formatNumber = (num: number | undefined) => {
  if (!num) return 0;

  const formattedAmount = num.toLocaleString('en-US', {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return formattedAmount;
};

export const formatCurrency = (amount: number | undefined) =>
  `$${formatNumber(amount)}`;

export const dataURLtoFile = (dataURL: string, filename: string) => {
  const arr = dataURL.split(',') as any;
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;

export const getNumDays = (dates: IDates): number => {
  const numMilliSeconds = differenceInMilliseconds(
    dates.endDate,
    dates.startDate
  );
  const days = numMilliSeconds / DAY_MILLISECONDS;

  if (days - Math.round(days) >= 0.05) {
    return Math.round(days) + 1;
  }

  return Math.round(numMilliSeconds / DAY_MILLISECONDS);
};

function toRadians(degrees: number) {
  return degrees * (Math.PI / 180);
}

function toDegrees(radians: number) {
  return radians * (180 / Math.PI);
}

export const midpoint = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  lat3?: number,
  lon3?: number
) => {
  if (lat3 !== undefined && lon3 !== undefined) {
    // Convert latitude and longitude from degrees to radians
    lat1 = toRadians(lat1);
    lon1 = toRadians(lon1);
    lat2 = toRadians(lat2);
    lon2 = toRadians(lon2);
    lat3 = toRadians(lat3);
    lon3 = toRadians(lon3);

    // Calculate the average of the latitudes and longitudes
    const latMid = (lat1 + lat2 + lat3) / 3;
    const lonMid = (lon1 + lon2 + lon3) / 3;

    // Convert midpoint from radians to degrees
    return {
      latitude: toDegrees(latMid),
      longitude: toDegrees(lonMid),
    };
  } else {
    // Convert latitude and longitude from degrees to radians
    lat1 = toRadians(lat1);
    lon1 = toRadians(lon1);
    lat2 = toRadians(lat2);
    lon2 = toRadians(lon2);

    // Calculate the differences
    const dLon = lon2 - lon1;

    // Calculate midpoint latitude in radians
    const Bx = Math.cos(lat2) * Math.cos(dLon);
    const By = Math.cos(lat2) * Math.sin(dLon);
    const latMid = Math.atan2(
      Math.sin(lat1) + Math.sin(lat2),
      Math.sqrt((Math.cos(lat1) + Bx) * (Math.cos(lat1) + Bx) + By * By)
    );

    // Calculate midpoint longitude in radians
    const lonMid = lon1 + Math.atan2(By, Math.cos(lat1) + Bx);

    // Convert midpoint from radians to degrees
    return {
      latitude: toDegrees(latMid),
      longitude: toDegrees(lonMid),
    };
  }
};

export const getDistanceInMiles = (
  { lat: lat1, lon: lon1 }: { lat: number; lon: number },
  { lat: lat2, lon: lon2 }: { lat: number; lon: number }
) => {
  const R = 3958.8; // Radius of Earth in miles

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export const isDesktop = () => {
  return ['md', 'lg'].includes(UnistylesRuntime.breakpoint);
};

export const generateTimes = (intervalMinutes = 30) => {
  const times = [];
  let currentTime = startOfDay(new Date()); // Start at 12:00 AM
  const endOfDay = addMinutes(currentTime, 24 * 60); // Add 24 hours' worth of minutes

  while (currentTime < endOfDay) {
    times.push(currentTime); // Format using date-fns
    currentTime = addMinutes(currentTime, intervalMinutes); // Increment by interval
  }

  return times;
};

type GoogleAddressComponent = {
  types: string[];
  longText?: string;
  shortText?: string;
  long_name?: string;
  short_name?: string;
};

export type ParsedAddressParts = {
  streetNumber: string;
  streetName: string;
  addressLine2: string;
  county: string;
  country: string;
};

// Normalizes Google Places address components (handles both the v1 Places API
// `longText`/`shortText` shape and the legacy geocoding `long_name`/`short_name`
// shape) into the discrete address fields the rental location API now expects.
export const parseAddressComponents = (
  components: GoogleAddressComponent[] | undefined | null
): ParsedAddressParts => {
  const result: ParsedAddressParts = {
    streetNumber: '',
    streetName: '',
    addressLine2: '',
    county: '',
    country: '',
  };

  if (!components) return result;

  const long = (c: GoogleAddressComponent) => c.longText || c.long_name || '';
  const short = (c: GoogleAddressComponent) =>
    c.shortText || c.short_name || '';

  components.forEach((component) => {
    const types = component?.types;
    if (!types) return;

    if (types.includes('street_number')) {
      result.streetNumber = long(component);
    }
    if (types.includes('route')) {
      result.streetName = long(component);
    }
    if (types.includes('subpremise')) {
      result.addressLine2 = long(component);
    }
    if (types.includes('administrative_area_level_2')) {
      result.county = long(component);
    }
    if (types.includes('country')) {
      // API expects a country code (e.g. "US")
      result.country = short(component);
    }
  });

  return result;
};

export const openAddressInMaps = (address: string) => {
  const encodedAddress = encodeURIComponent(address);
  const url =
    Platform.OS === 'ios'
      ? `http://maps.apple.com/?q=${encodedAddress}`
      : `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

  Linking.openURL(url).catch((err) => console.error('An error occurred', err));
};

export const rgbaToHex = (orig) => {
  var a,
    isPercent,
    rgb = orig
      .replace(/\s/g, '')
      .match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i),
    alpha = ((rgb && rgb[4]) || '').trim(),
    hex = rgb
      ? (rgb[1] | (1 << 8)).toString(16).slice(1) +
        (rgb[2] | (1 << 8)).toString(16).slice(1) +
        (rgb[3] | (1 << 8)).toString(16).slice(1)
      : orig;

  if (alpha !== '') {
    a = alpha;
  } else {
    a = '0o1';
  }
  // multiply before convert to HEX
  a = ((a * 255) | (1 << 8)).toString(16).slice(1);
  hex = hex + a;

  return '#' + hex;
};

export const uniqByFilename = (imageObjs) => {
  const seen = new Set();

  return imageObjs.filter((img) => {
    const fileName = img.fileName || img.name;
    const isDuplicate = seen.has(fileName);
    seen.add(fileName);

    return !isDuplicate;
  });
};

export const validatePhoneNumber = (
  country: PhoneCountry | undefined,
  phone: string
) => {
  if (!country || !phone) return false;

  try {
    const phoneNumber = parsePhoneNumberWithError(phone, {
      defaultCallingCode: country.callingCode,
    });

    if (!phoneNumber) return false;

    return phoneNumber.isValid() === true;
  } catch (e: any) {
    return false;
  }
};

export const getCarImages = (
  images: CarImages,
  size?: 'small' | 'medium' | 'large' | 'extraLarge'
): CarImage[] => {
  const fallbackImg =
    UnistylesRuntime.themeName === 'dark'
      ? require('../../public/images/cars/fallback-car-dark.png')
      : require('../../public/images/cars/fallback-car-light.png');

  const fallbackUrl =
    Platform.OS === 'web'
      ? fallbackImg.uri
      : Image.resolveAssetSource(fallbackImg).uri;

  if (!images) {
    return [
      {
        sortIndex: 0,
        url: fallbackUrl,
      },
    ];
  }

  const imagesByTheme = images[UnistylesRuntime.themeName];

  if (!imagesByTheme) {
    return [
      {
        sortIndex: 0,
        url: fallbackUrl,
      },
    ];
  }

  if (size) {
    return imagesByTheme[size];
  }

  switch (UnistylesRuntime.breakpoint) {
    case 'xs':
      return (
        imagesByTheme.small ||
        imagesByTheme.medium ||
        imagesByTheme.large ||
        imagesByTheme.extraLarge
      );
    case 'sm':
      return (
        imagesByTheme.medium || imagesByTheme.large || imagesByTheme.extraLarge
      );
    case 'md':
      return imagesByTheme.large || imagesByTheme.extraLarge;
    case 'lg':
      return imagesByTheme.extraLarge;
    default:
      return [];
  }
};

export const getPricingHistogram = (modelName: string) => {
  const themeName = UnistylesRuntime.themeName;

  switch (modelName) {
    case 'model_3':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-3-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/model-3-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-3-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/model-3-histogram-mobile.png`);
      }
    case 'model_y':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-y-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/model-y-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-y-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/model-y-histogram-mobile.png`);
      }
    case 'cybertruck':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/cybertruck-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/cybertruck-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/cybertruck-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/cybertruck-histogram-mobile.png`);
      }
    case 'model_x':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-x-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/model-x-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-x-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/model-x-histogram-mobile.png`);
      }
    case 'model_s':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-s-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/model-s-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/model-s-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/model-s-histogram-mobile.png`);
      }
    case 'air':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/air-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/air-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/air-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/air-histogram-mobile.png`);
      }
    case 'r1t':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/r1t-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/r1t-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/r1t-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/r1t-histogram-mobile.png`);
      }
    case 'r1s':
      if (isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/r1s-histogram.png`);
      } else if (isDesktop() && themeName === 'light') {
        return require(`../../public/images/partners/pricing-histograms/light/r1s-histogram.png`);
      } else if (!isDesktop() && themeName === 'dark') {
        return require(`../../public/images/partners/pricing-histograms/dark/r1s-histogram-mobile.png`);
      } else {
        return require(`../../public/images/partners/pricing-histograms/light/r1s-histogram-mobile.png`);
      }
    default:
      break;
  }
};

export const getColorImgSrc = (color: string) => {
  switch (color) {
    case 'black':
      return require('../../public/images/colors/black.png');
    case 'blue':
      return require('../../public/images/colors/blue.png');
    case 'white':
      return require('../../public/images/colors/white.png');
    case 'red':
      return require('../../public/images/colors/red.png');
    case 'silver':
      return require('../../public/images/colors/silver.png');
    case 'grey':
      return require('../../public/images/colors/grey.png');
    default:
      break;
  }
};

export const getCarBrandLogo = (brandLogos: any) => {
  const isDarkMode = UnistylesRuntime.themeName === 'dark';

  return isDarkMode ? brandLogos.dark : brandLogos.light;
};

export const compressAndResize = async (
  asset: ImagePicker.ImagePickerAsset | DocumentPicker.DocumentPickerAsset
) => {
  const isImagePickerAsset = 'width' in asset;

  const width = isImagePickerAsset ? Math.min(asset.width, 1000) : 1000;

  const compressedImage = await ImageManipulator.manipulateAsync(
    asset.uri,
    [{ resize: { width: width } }],
    {
      compress: 0.6,
      format: ImageManipulator.SaveFormat.JPEG,
    }
  );

  return compressedImage;
};

export const UPLOAD_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'application/pdf',
];

export const prepareAssetForUpload = (
  asset: ImagePicker.ImagePickerAsset | DocumentPicker.DocumentPickerAsset
) => {
  if (asset.mimeType === 'application/pdf') {
    return Promise.resolve({ uri: asset.uri });
  }
  return compressAndResize(asset);
};

export const runWithConcurrencyLimit = async (
  tasks: any[],
  concurrency = 2
) => {
  const results = [];
  const executing: any[] = [];

  for (const task of tasks) {
    const p = task().then((result: any) => {
      executing.splice(executing.indexOf(p), 1); // remove from active list when done
      return result;
    });
    results.push(p);
    executing.push(p);

    if (executing.length >= concurrency) {
      await Promise.race(executing); // wait for one to finish
    }
  }

  return Promise.all(results);
};

export function getMobileWebOS() {
  if (Platform.OS !== 'web') return null;

  const ua = navigator.userAgent;

  if (/android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';

  return 'other';
}

// --- Time zone helpers --------------------------------------------------
// All formatters use date-fns format tokens. Wrap literal text in single
// quotes (e.g. "'UTC'"). For zone abbreviations use `zzz` ("EST"), `zzzz`
// ("Eastern Standard Time"), or `OOOO` ("GMT-05:00").

const OFFSET_PRESERVING_ISO = "yyyy-MM-dd'T'HH:mm:ss.SSSXXX";

/**
 * Treat a calendar day as UTC midnight and return the ISO string. Use as
 * the START boundary of a date-range filter so the boundary doesn't shift
 * across midnight based on the viewer's local time zone.
 */
export const toUTCStartOfDayISO = (d: Date | null) =>
  d
    ? new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
      ).toISOString()
    : undefined;

/**
 * Treat a calendar day as the last millisecond of that UTC day and return
 * the ISO string. Use as the END boundary of a date-range filter.
 */
export const toUTCEndOfDayISO = (d: Date | null) =>
  d
    ? new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
      ).toISOString()
    : undefined;

/**
 * Return the calendar date the user picked as a plain "yyyy-MM-dd" string,
 * with no time and no time-zone conversion. Use for date-range filters
 * where the backend compares each record's own local date against the
 * range (so the displayed picker date is sent verbatim).
 */
export const toDateOnlyString = (d: Date | null) =>
  d ? format(d, 'yyyy-MM-dd') : undefined;

/**
 * Render an ISO timestamp in a specific IANA time zone (e.g.
 * "America/New_York"), ignoring the viewer's local time zone. Use to
 * display a backend instant in a record's own zone, such as a rental's
 * pickup location.
 */
export const formatIsoInZone = (
  iso: string,
  tz: string,
  pattern: string
): string => formatInTimeZone(new Date(iso), tz, pattern);

/**
 * Render an ISO timestamp as UTC, ignoring the viewer's local time zone.
 */
export const formatInUTC = (iso: string, pattern: string): string =>
  formatIsoInZone(iso, 'UTC', pattern);

/**
 * Render an ISO timestamp in whatever UTC offset its string already carries
 * (e.g. "-05:00", "-04:00", "Z"), preserving the backend's zone intent.
 */
export const formatInOriginalOffset = (
  iso: string,
  pattern: string
): string => {
  const match = iso.match(/(Z|[+-]\d{2}:?\d{2})$/);
  const tz = !match || match[1] === 'Z' ? 'UTC' : match[1];
  return formatInTimeZone(new Date(iso), tz, pattern);
};

/**
 * Interpret a JS Date's calendar fields as a wall-clock time in `tz` (an
 * IANA name like "America/New_York") and return an ISO string carrying
 * that offset. Use for date-picker submissions that should be saved in a
 * fixed zone (e.g. a rental's pickup location) instead of the viewer's
 * local zone.
 */
export const toZonedISO = (d: Date, tz: string): string =>
  formatInTimeZone(fromZonedTime(d, tz), tz, OFFSET_PRESERVING_ISO);

/**
 * Display a JS Date whose calendar fields represent a wall-clock time in
 * `tz`. Use alongside `toZonedISO` so the picker, submit, and re-render
 * all agree on the zone.
 */
export const formatInZone = (d: Date, tz: string, pattern: string): string =>
  formatInTimeZone(fromZonedTime(d, tz), tz, pattern);

export const BASE_DELIVERY_EARNINGS = 127.5;

export function getEstimatedMaxDeliveryFee(
  maxDeliveryDistance?: string | number,
  deliveryDistanceCostOptions?: Record<string, number>
): number {
  if (!maxDeliveryDistance) return 0;

  const distanceCost =
    deliveryDistanceCostOptions?.[String(maxDeliveryDistance)] ?? 0;

  return BASE_DELIVERY_EARNINGS + distanceCost;
}
